import * as Sentry from '@sentry/vue'
import { readAcquisitionCookie } from '@/utils/segment.utils'

// One-shot flush of the anonymous acquisition signal log (ahaSegment cookie,
// written by GTM on the landing site) into personalInformation.acquisition.
// Watched paths:
// - setCurrentUserAction: interactive login (LoginV3) and signup (RegisterV3;
//   also the RegisterV2 guest conversion and MSTeams config flows, where the
//   guards below make this a no-op).
// - getCurrentUserAction: app load with canUseCacheProfileOnLoad OFF.
// - loadingPage/fetchAndSaveCurrentUserAction: app load with the flag ON
//   (App.vue goes through loadingPage/getCurrentUserWithCacheAction, which
//   never dispatches getCurrentUserAction), so returning users still flush on
//   reload. When it is reached through getCurrentUserAction's internal cache
//   branch both hooks fire; the guards below keep the patch single.
// AHA-43253.
const WATCHED_ACTIONS = [
  'getCurrentUserAction',
  'setCurrentUserAction',
  'loadingPage/fetchAndSaveCurrentUserAction',
]

// In-flight latch: prevents a double flush when a watched action fires again
// before the first PATCH resolves (the already-flushed guard would still read
// stale state at that point).
let flushPromise = null

const acquisitionFlushPlugin = (store) => {
  store.subscribeAction({
    after: async (action) => {
      if (!WATCHED_ACTIONS.includes(action.type)) return
      try {
        if (!store.getters['featureToggles/canUseAcquisitionSignalLog']) return
        const { currentUser } = store.state
        if (!currentUser?.id || currentUser.isGuest) return
        if (currentUser.personalInformation?.acquisition) return
        const log = readAcquisitionCookie()
        if (!log) return
        if (flushPromise) return
        // Send only the acquisition delta; the backend merges
        // personalInformation per key, so concurrent writers of other keys
        // are not clobbered.
        flushPromise = store
          .dispatch('patchCurrentUserAction', {
            userId: currentUser.id,
            personalInformation: { acquisition: log },
          })
          .finally(() => {
            flushPromise = null
          })
        await flushPromise
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'acquisition-flush-plugin',
            userId: store.state.currentUser?.id,
            actionType: action.type,
          },
        })
      }
    },
  })
}

export default acquisitionFlushPlugin
